<template>
	<div class="custom-tabs">
		<!-- Tabs -->
		<div
			class="custom-tabs-nav"
			:class="[isMobile ? 'ex2rem-overflow-padding ex2rem-content-padding' : '']"
			ref="navRef"
			@scroll="handleScroll"
		>
			<div class="custom-tabs-nav-list" ref="navListRef">
				<div
					v-for="tab in tabs"
					:key="tab.key"
					:ref="el => setTabRef(el, tab.key)"
					:class="['custom-tabs-nav-item', { 'custom-tabs-nav-item-active': currentValue === tab.key }]"
					@click="handleTabClick(tab.key)"
				>
					<span>{{ tab.tab }}</span>
				</div>
				<div class="custom-tabs-slider" :style="sliderStyle"></div>
			</div>
		</div>

		<!-- Mobile Mask -->
		<div v-if="isMobile" class="custom-tabs-mask-container ex2rem-overflow-padding">
			<!-- 左侧蒙层 -->
			<div v-show="isMobile && showLeftMask" class="custom-tabs-mask custom-tabs-mask-left"></div>
			<!-- 右侧蒙层 -->
			<div v-show="isMobile && showRightMask" class="custom-tabs-mask custom-tabs-mask-right"></div>
		</div>

		<!-- Content -->
		<div class="custom-tabs-content">
			<div class="custom-tabs-content-item">
				<slot />
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from '@/utils/vue-api';
import { debounce } from '@/utils/api';
import i18n from '@/locales';
import { RESIZE_DEBOUNCE_TIME } from '@/utils/const';

const props = defineProps({
	tabs: {
		type: Array,
		required: true
	},
	value: {
		type: [String, Number],
		required: true
	}
});
const emit = defineEmits(['update:value']);

const currentValue = computed({
	get() {
		return props.value;
	},
	set(newValue) {
		emit('update:value', newValue);
	}
});

const store = useStore();
const isMobile = computed(() => store.state.isMobile);
const currentLocale = computed(() => i18n.locale);

const navRef = ref(null);
const navListRef = ref(null);
const tabRefs = ref({});
const sliderStyle = ref({
	width: '0px',
	left: '0px'
});

const setTabRef = (el, key) => {
	if (el) {
		tabRefs.value[key] = el;
	}
};
const updateSlider = () => {
	nextTick(() => {
		const activeTab = tabRefs.value[currentValue.value];
		if (!activeTab || !navListRef.value) return;

		// 使用 offsetLeft 获取相对于父元素的位置
		const left = activeTab.offsetLeft;
		const width = activeTab.offsetWidth;

		sliderStyle.value = {
			width: `${width}px`,
			left: `${left}px`
		};
	});
};

const showLeftMask = ref(false);
const showRightMask = ref(false);
const updateMaskVisibility = () => {
	if (!isMobile.value || !navRef.value || !navListRef.value) {
		showLeftMask.value = false;
		showRightMask.value = false;
		return;
	}

	// 滚动容器是 navRef，内容在 navListRef
	const containerWidth = navRef.value.clientWidth; // 容器的可见宽度
	const contentWidth = navListRef.value.scrollWidth; // 内容的实际宽度
	const scrollLeft = navRef.value.scrollLeft; // 滚动位置

	const canScroll = contentWidth > containerWidth;
	if (!canScroll) {
		// 内容未超出，不需要显示蒙层
		showLeftMask.value = false;
		showRightMask.value = false;
		return;
	}

	// 检测是否在左侧终点（允许 1px 的误差）
	const isAtLeft = scrollLeft <= 1;
	// 检测是否在右侧终点（允许 1px 的误差）
	const isAtRight = scrollLeft + containerWidth >= contentWidth - 1;

	showLeftMask.value = !isAtLeft;
	showRightMask.value = !isAtRight;
};

const handleScroll = () => {
	updateMaskVisibility();
};
watch(
	() => [currentValue.value, props.tabs, currentLocale.value],
	() => {
		updateSlider();
		nextTick(() => {
			updateMaskVisibility();
		});
	},
	{ immediate: true, deep: true }
);

const onResize = debounce(() => {
	updateSlider();
	updateMaskVisibility();
}, RESIZE_DEBOUNCE_TIME);
onMounted(() => {
	updateSlider();
	updateMaskVisibility();
	window.addEventListener('resize', onResize);
});
onBeforeUnmount(() => {
	window.removeEventListener('resize', onResize);
});

const scrollToTab = tabElement => {
	if (!isMobile.value || !navRef.value || !tabElement) return;

	const container = navRef.value;
	const containerRect = container.getBoundingClientRect();
	const tabRect = tabElement.getBoundingClientRect();

	// 计算 Item 相对于容器的位置（考虑滚动位置）
	const tabLeft = tabRect.left - containerRect.left + container.scrollLeft;
	const tabRight = tabLeft + tabRect.width;
	const containerScrollLeft = container.scrollLeft;
	const containerWidth = container.clientWidth;
	const containerScrollRight = containerScrollLeft + containerWidth;

	// 添加边距，避免 Item 紧贴边缘（移动端 gap 是 20px，使用一半作为边距）
	const padding = 10;

	// 判断是否需要滚动
	let targetScrollLeft = containerScrollLeft;
	let needScroll = false;

	// 如果 Item 在可视区域左侧外，滚动到 Item 左边缘对齐（带边距）
	if (tabLeft < containerScrollLeft + padding) {
		targetScrollLeft = tabLeft - padding;
		needScroll = true;
	}
	// 如果 Item 在可视区域右侧外，滚动到 Item 右边缘对齐（带边距）
	else if (tabRight > containerScrollRight - padding) {
		targetScrollLeft = tabRight - containerWidth + padding;
		needScroll = true;
	}

	// 如果不需要滚动，直接返回
	if (!needScroll) {
		return;
	}

	// 确保滚动位置在有效范围内
	const maxScrollLeft = container.scrollWidth - containerWidth;
	targetScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));

	// 平滑滚动
	container.scrollTo({
		left: targetScrollLeft,
		behavior: 'smooth'
	});
};
// 智能滚动计算
const smartScrollToTab = key => {
	nextTick(() => {
		const clickedTab = tabRefs.value[key];
		if (clickedTab) {
			scrollToTab(clickedTab);
		}
	});
};
// 监听语言变化，智能滚动到当前选中的tab
watch(
	() => currentLocale.value,
	() => {
		if (!isMobile.value || !navRef.value) return;
		smartScrollToTab(currentValue.value);
	}
);

const handleTabClick = key => {
	currentValue.value = key;

	if (isMobile.value) {
		smartScrollToTab(key);
	}
};
</script>

<style scoped lang="less">
@transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
@transition-duration: 0.3s;

.custom-tabs {
	position: relative;
}

.custom-tabs-nav {
	overflow-x: auto;
	overflow-y: hidden;
	-webkit-overflow-scrolling: touch;
	scrollbar-width: none; /* Firefox */
	-ms-overflow-style: none; /* IE and Edge */

	&::-webkit-scrollbar {
		display: none; /* Chrome, Safari, Opera */
	}
}

.custom-tabs-nav-list {
	display: flex;
	align-items: flex-end;
	justify-content: flex-start;
	border-bottom: 1px solid var(--border-normal-color);
	min-width: min-content;
	gap: 48px;
	position: relative;
}

.custom-tabs-nav-item {
	padding-bottom: 12px;
	cursor: pointer;
	color: var(--investors-osl-color-black);
	font-size: 16px;
	font-weight: 400;
	line-height: 24px;
	white-space: nowrap;
	user-select: none;
	transition: color @transition-duration @transition-timing-function;

	&:hover {
		color: var(--investors-osl-color-black);
	}

	&.custom-tabs-nav-item-active {
		font-weight: 700;
	}
}

.custom-tabs-slider {
	position: absolute;
	bottom: -1px;
	height: 2px;
	background-color: var(--investors-osl-color-black);
	transition: left @transition-duration @transition-timing-function,
		width @transition-duration @transition-timing-function;
	will-change: left, width;
}

.custom-tabs-content {
	position: relative;
}

.custom-tabs-content-item {
	width: 100%;
	min-height: 200px;
	padding-top: 40px;
	box-sizing: border-box;
}

@media (max-width: @device-width-mobile) {
	.custom-tabs-nav-list {
		gap: 20px;
	}

	.custom-tabs-nav-item {
		font-size: 14px;
		line-height: 18px;
		color: #86909c;

		&.custom-tabs-nav-item-active {
			font-weight: 500;
			color: var(--investors-osl-color-black);
		}
	}

	.custom-tabs-content-item {
		padding-top: 32px;
		min-height: 150px;
	}

	.custom-tabs-mask-container {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 26px;
		pointer-events: none;
		z-index: 1;
	}
	.custom-tabs-mask {
		position: absolute;
		top: 0;
		bottom: 0;
		width: 40px;
		transition: opacity @transition-duration @transition-timing-function;
	}
	.custom-tabs-mask-left {
		left: 0;
		background: linear-gradient(to right, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
	}
	.custom-tabs-mask-right {
		right: 0;
		background: linear-gradient(to left, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
	}
}
</style>
